import { useState, useEffect } from 'react';
import ArtZipButton from './ArtZipButton';

/**
 * Deep-link target for a single batch's downloads — opened by `?batch=LABEL`,
 * which is the link the confirmation email uses when a file was too large to
 * attach. Production lands straight on the files for their batch instead of
 * scrolling the dashboard hunting for the right row.
 *
 * Renders ahead of the dashboard's data load so the link opens immediately.
 */
export default function BatchDownloadModal({ label, onClose }) {
  const [manifest, setManifest] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/greenlight/export/${encodeURIComponent(label)}/art-manifest`)
      .then(async (r) => {
        const d = await r.json();
        if (cancelled) return;
        if (!r.ok) setError(d.error || 'Could not load this batch');
        else setManifest(d);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load this batch');
      });
    return () => {
      cancelled = true;
    };
  }, [label]);

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="px-6 py-4 border-b border-gray-100 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900">Batch downloads</h2>
            <p className="text-xs font-mono text-gray-500 mt-0.5 truncate">{label}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none flex-shrink-0">
            ×
          </button>
        </div>

        <div className="px-6 py-5">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 mb-4">
              {error}
            </div>
          )}

          {!error && !manifest && <p className="text-sm text-gray-400 mb-4">Loading batch…</p>}

          {manifest && (
            <p className="text-sm text-gray-600 mb-4">
              <span className="font-semibold text-gray-900">{manifest.orderCount}</span> order
              {manifest.orderCount !== 1 ? 's' : ''} ·{' '}
              <span className="font-semibold text-gray-900">{manifest.fileCount}</span> QR art file
              {manifest.fileCount !== 1 ? 's' : ''}
            </p>
          )}

          {manifest && !manifest.ok && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-4">
              <p className="text-xs font-semibold text-amber-800 mb-1">
                This batch has issues — the art zip is blocked until they're fixed:
              </p>
              <ul className="text-xs text-amber-800 font-mono space-y-0.5 max-h-40 overflow-y-auto">
                {manifest.issues.slice(0, 20).map((issue, i) => (
                  <li key={i}>{issue}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <ArtZipButton
              label={label}
              className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors inline-flex items-center gap-1.5"
            >
              <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
              </svg>
              Download QR art ZIP
            </ArtZipButton>

            <a
              href={`/api/greenlight/export/${encodeURIComponent(label)}`}
              download
              className="px-4 py-2 border border-gray-300 bg-white text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors inline-flex items-center gap-1.5"
            >
              Download order CSV
            </a>
          </div>

          <p className="text-xs text-gray-400 mt-4">
            The zip is assembled in your browser, so keep this tab open until it finishes.
          </p>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
